/** Pillar keys and display labels — aligned with Insights API / backend. */
export const PILLAR_KEYS = [
  { key: "strategic", label: "Strategic clarity" },
  { key: "scalable", label: "Scalable talent" },
  { key: "relentless", label: "Relentless focus" },
  { key: "disciplined", label: "Disciplined execution" },
  { key: "energized", label: "Energized culture" },
  { key: "financial", label: "Financial performance" },
];

/** Pillar charts only (no financial). */
export const PILLAR_KEYS_FOR_CHARTS = PILLAR_KEYS.filter((p) => p.key !== "financial");

// ---------------------------------------------------------------------------
// Generic / custom-type helpers — Track A
// ---------------------------------------------------------------------------

/**
 * Derive the active pillar/section keys for the current workspace.
 *
 * When ANY org in the array carries a non-empty ``dimensions`` list the
 * workspace is using a custom assessment type, so we build the key list from
 * those dimensions (preserving the order the tenant defined).
 *
 * Falls back to the classic PILLAR_KEYS_FOR_CHARTS for 5X workspaces or when
 * the API hasn't been updated yet.
 *
 * @param {Array} organizations - raw org rows from the Insights API
 * @returns {{ key: string, label: string }[]}
 */
export function getActivePillarKeys(organizations) {
  const withDims = (organizations || []).find(
    (o) => Array.isArray(o.dimensions) && o.dimensions.length > 0
  );
  if (withDims) {
    return withDims.dimensions.map((d) => ({ key: d.key, label: d.label }));
  }
  return PILLAR_KEYS_FOR_CHARTS;
}

/**
 * Read a single section/pillar score from an org row.
 *
 * 5X orgs store scores as flat top-level properties (``org.strategic`` etc.).
 * Custom-type orgs store them in ``org.dimensions[i].score``.
 * This helper handles both transparently.
 *
 * @param {object} org
 * @param {string} key  - pillar/section key
 * @returns {number|null}
 */
export function getPillarScore(org, key) {
  if (Array.isArray(org?.dimensions) && org.dimensions.length > 0) {
    return org.dimensions.find((d) => d.key === key)?.score ?? null;
  }
  return org?.[key] ?? null;
}

/**
 * Read importance score for a section/pillar from an org row.
 * @param {object} org
 * @param {string} key
 * @returns {number|null}
 */
export function getPillarImportance(org, key) {
  if (Array.isArray(org?.dimensions) && org.dimensions.length > 0) {
    return org.dimensions.find((d) => d.key === key)?.importance ?? null;
  }
  return org?.importanceByPillar?.[key] ?? null;
}

/**
 * Read agreement score for a section/pillar from an org row.
 * @param {object} org
 * @param {string} key
 * @returns {number|null}
 */
export function getPillarAgreement(org, key) {
  if (Array.isArray(org?.dimensions) && org.dimensions.length > 0) {
    return org.dimensions.find((d) => d.key === key)?.agreement ?? null;
  }
  return org?.agreementByPillar?.[key] ?? null;
}

/**
 * True when the scoped orgs mix classic flat pillars with custom ``dimensions``.
 * Callers should show a mismatch notice instead of averaging unlike structures.
 */
export function hasAssessmentStructureMismatch(organizations) {
  const orgs = (organizations || []).filter((o) => o?.reportReady);
  if (orgs.length < 2) return false;
  let hasDims = false;
  let hasClassic = false;
  for (const o of orgs) {
    if (Array.isArray(o.dimensions) && o.dimensions.length > 0) hasDims = true;
    else if (PILLAR_KEYS_FOR_CHARTS.some(({ key }) => o[key] != null)) hasClassic = true;
    if (hasDims && hasClassic) return true;
  }
  // Different custom dimension key sets
  const dimKeySets = orgs
    .filter((o) => Array.isArray(o.dimensions) && o.dimensions.length > 0)
    .map((o) => o.dimensions.map((d) => d.key).join("|"));
  if (dimKeySets.length >= 2 && new Set(dimKeySets).size > 1) return true;
  return false;
}

/** Whether an org has importance scores (classic map or dimensions). */
export function orgHasImportanceScores(org) {
  if (!org?.reportReady) return false;
  if (Array.isArray(org.dimensions) && org.dimensions.some((d) => d.importance != null)) {
    return true;
  }
  return Boolean(org.importanceByPillar);
}

/** Whether an org has agreement scores (classic map or dimensions). */
export function orgHasAgreementScores(org) {
  if (!org?.reportReady) return false;
  if (Array.isArray(org.dimensions) && org.dimensions.some((d) => d.agreement != null)) {
    return true;
  }
  return Boolean(org.agreementByPillar);
}
